import { format } from "date-fns";
import * as React from "react";
import type { DateRange as DateRangeProps, DayPickerProps } from "react-day-picker";

import { faCalendarDays } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faXmarkCircle } from "@performyard/fontawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@performyard/fontawesome/react-fontawesome";

import { cn } from "../../../lib/utils";

// components
import { ButtonIcon } from "../../atoms/ButtonIcon";
import { inputContainerVariants } from "../../atoms/InputBase";
import { Calendar } from "../../molecules/Calendar";
import { useInputField } from "../../molecules/InputWrapper/InputContext";
import { Popover, PopoverContent, PopoverTrigger } from "../../molecules/Popover";

/**
 * DateRange Type
 *
 * Represents a date range with optional start and end dates.
 * This type is re-exported from react-day-picker for convenience.
 *
 * @typedef {Object} DateRange
 * @property {Date | undefined} from - The start date of the range (inclusive)
 * @property {Date | undefined} to - The end date of the range (inclusive, optional)
 *
 * The DateRange can be in three states:
 * 1. Complete range: both 'from' and 'to' are defined
 * 2. Open range: only 'from' is defined, 'to' is undefined
 * 3. No range: both 'from' and 'to' are undefined
 */

/**
 * Props for the DateRangePicker component
 */
export interface DatePickerWithRangeProps {
	/** Controls the alignment of the popover relative to the trigger button */
	menuAlign?: "start" | "end" | "center";
	/** Custom CSS class for the popover trigger element */
	popoverTriggerClassName?: string;
	/** Custom CSS class for the popover content element */
	popoverContentClassName?: string;
	/** The currently selected date range */
	value?: DateRange;
	/** Whether to show a clear button when a date range is selected */
	hasClearButton?: boolean;
	/** Callback fired when the date range selection changes */
	onChange: (date: DateRange | undefined) => void;
	/** Callback fired when the popover opens or closes */
	onOpenChange?: (isOpen: boolean) => void;
	/** The id of the date range picker */
	id?: string;
	/** The test id of the date range picker */
	"data-testid"?: string;
	/** Whether the date range picker is errored */
	errored?: boolean;
	/** Whether the date range picker is disabled */
	disabled?: boolean;
	/** The start date to display */
	startDate?: Date;
	/** The end date to display */
	endDate?: Date;
}

export type DateRange = DateRangeProps;

/**
 * DateRangePicker Component
 *
 * A customizable date range picker component that allows users to select a range of dates.
 * Built on top of react-day-picker with additional styling and functionality.
 *
 * @param props - The component props including DatePickerWithRangeProps and DayPickerProps
 * @returns A React component for selecting date ranges
 *
 * @example
 * ```tsx
 * const [dateRange, setDateRange] = useState<DateRange | undefined>();
 *
 * <DateRangePicker
 *   value={dateRange}
 *   onChange={setDateRange}
 *   menuAlign="center"
 *   hasClearButton={true}
 * />
 * ```
 */
export function DateRangePicker(props: DatePickerWithRangeProps & Omit<DayPickerProps, "disabled">) {
	const {
		menuAlign = "end",
		popoverTriggerClassName,
		popoverContentClassName,
		startDate,
		endDate,
		value,
		hasClearButton,
		onChange,
		onOpenChange,
		id: propId,
		"data-testid": testId,
		disabled: disabledProp = false,
		errored: erroredProp = false,
	} = props;

	const { id: contextId, error, disabled: contextDisabled } = useInputField();

	const disabled = disabledProp || contextDisabled;
	const errored = erroredProp || !!error;
	const id = propId || contextId;

	const handleClearDate = () => {
		onChange({ from: undefined, to: undefined });
		//focus on the trigger button again after the clear button is pressed
		const trigger = document.getElementById(`${id}`);
		trigger?.focus();
	};

	return (
		<div className={cn("grid gap-2")} data-testid={testId} id={id}>
			<Popover onOpenChange={onOpenChange}>
				<PopoverTrigger asChild disabled={disabled}>
					<button
						id={`${id}`}
						type="button"
						tabIndex={0}
						aria-label="Select date range"
						aria-expanded={false}
						aria-haspopup="dialog"
						disabled={disabled}
						className={cn(
							inputContainerVariants({
								errored,
								disabled,
							}),
							"box-border flex justify-between rounded bg-neutral-0 text-left",
							popoverTriggerClassName,
							errored && "border-red-6"
						)}
						data-testid={`${id}-trigger`}
					>
						{value?.from ? (
							value.to ? (
								<span className="text-paragraph-3 font-regular">
									{format(value.from, "LLL d, y")} - {format(value.to, "LLL d, y")}
								</span>
							) : (
								<span className="text-paragraph-3 font-regular">{format(value.from, "LLL d, y")}</span>
							)
						) : (
							<span className="text-paragraph-3 font-regular text-neutral-5">Start Date - End Date</span>
						)}
						<div className="flex gap-2">
							{hasClearButton && value?.from && (
								<ButtonIcon
									icon={faXmarkCircle}
									size={16}
									color="neutral"
									shade={3}
									onClick={handleClearDate}
									disabled={disabled}
									aria-disabled={disabled}
									aria-label="Clear date range"
									data-testid={`${id}-clear-button`}
								/>
							)}

							<div className="flex size-4 items-center justify-center text-neutral-5" aria-hidden="true">
								<FontAwesomeIcon icon={faCalendarDays} />
							</div>
						</div>
					</button>
				</PopoverTrigger>
				<PopoverContent
					className={cn("w-auto p-0", popoverContentClassName)}
					align={menuAlign}
					sideOffset={8}
					role="dialog"
					aria-modal="true"
					aria-label="Date range picker"
				>
					<Calendar
						mode="range"
						defaultMonth={value?.from}
						selected={value}
						onSelect={(range) => {
							const dateRange = range as DateRange | undefined;
							onChange(dateRange ?? { from: undefined, to: undefined });
						}}
						numberOfMonths={2}
						startDate={startDate}
						endDate={endDate}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
}

DateRangePicker.displayName = "DateRangePicker";
